"use client";

// ============================================================
// CLUBHUB – Spendenaufruf anlegen, bearbeiten, beenden
// ============================================================
// Ein Aufruf ist ein Ziel mit Zeitraum und Zweck. „Auf der Website
// zeigen“ stellt ihn mit Fortschrittsbalken auf die Vereinsseite;
// Spendernamen stehen dort nur mit Einwilligung „Internet“ und nie
// bei anonymen Spenden.
// ============================================================

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { CirclePause, CirclePlay, Pencil, Plus, Trash2 } from "lucide-react";
import {
  AmountInput,
  Button,
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogRow,
  DialogSubmit,
  DialogTitle,
  DropdownItem,
  DropdownMenu,
  DropdownSeparator,
  Input,
  MehrKnopf,
  Switch,
  Textarea,
  confirmDialog,
  toast,
} from "@/components/ui";
import { cent } from "@/lib/spenden/format";
import type { AufrufView } from "@/lib/spenden/views";
import { aufrufAktivSetzen, aufrufLoeschen, aufrufSpeichern } from "@/app/(app)/spenden/actions";

export function AufrufDialog({ open, onClose, aufruf }: { open: boolean; onClose: () => void; aufruf?: AufrufView | null }) {
  const router = useRouter();
  const [oeffentlich, setOeffentlich] = useState(aufruf?.isPublic ?? false);
  const [fehler, setFehler] = useState<Record<string, string>>({});

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogForm
        action={(fd) => aufrufSpeichern(aufruf?.id ?? null, fd)}
        success={aufruf ? "Aufruf gespeichert" : "Aufruf angelegt"}
        onSuccess={(r) => {
          if (!aufruf && "id" in r) router.push(`/spenden/aufrufe/${r.id}`);
        }}
        onError={(message, result) => {
          const field = result && "field" in result ? result.field : undefined;
          if (field) setFehler({ [field]: message });
          else toast.error(message);
        }}
      >
        <DialogHeader>
          <DialogTitle>{aufruf ? "Aufruf bearbeiten" : "Spendenaufruf anlegen"}</DialogTitle>
          <DialogDescription>Wofür gesammelt wird, bis wann und wie viel gebraucht wird.</DialogDescription>
        </DialogHeader>
        <DialogContent>
          <Input name="title" label="Titel" defaultValue={aufruf?.title ?? ""} placeholder="Neue Trikots für die Jugend" required maxLength={120} error={fehler.title} />
          <Input
            name="purpose"
            label="Zweck"
            defaultValue={aufruf?.purpose ?? ""}
            placeholder="Trikotsätze für U13 und U17"
            hint="Wird bei jeder Spende des Aufrufs als Zweck vorgeschlagen."
            maxLength={200}
          />
          <Textarea
            name="description"
            label="Beschreibung"
            defaultValue={aufruf?.description ?? ""}
            rows={4}
            placeholder="Warum der Verein sammelt — so steht es auf der Website und in der Nachricht."
          />
          <AmountInput name="goalAmount" label="Ziel (€)" defaultCents={cent(aufruf?.goalAmount)} className="numeric text-right" hint="Leer lassen, wenn es kein festes Ziel gibt." error={fehler.goalAmount} />
          <DialogRow>
            <Input name="startsAt" type="date" label="Beginn" defaultValue={aufruf?.startsAt ?? ""} error={fehler.startsAt} />
            <Input name="endsAt" type="date" label="Ende" defaultValue={aufruf?.endsAt ?? ""} error={fehler.endsAt} />
          </DialogRow>
          <input type="hidden" name="isPublic" value={oeffentlich ? "on" : ""} />
          <Switch
            checked={oeffentlich}
            onChange={setOeffentlich}
            label="Auf der Website zeigen"
            hint="Mit Fortschrittsbalken auf der Vereinsseite. Namen von Spendern erscheinen nur mit ihrer Einwilligung und nie bei anonymen Spenden."
          />
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>{aufruf ? "Speichern" : "Aufruf anlegen"}</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

export function AufrufAnlegenKnopf({ variant = "default" }: { variant?: "default" | "outline" }) {
  const [offen, setOffen] = useState(false);
  return (
    <>
      <Button variant={variant} onClick={() => setOffen(true)}>
        <Plus className="h-4 w-4" aria-hidden />
        Aufruf anlegen
      </Button>
      {offen && <AufrufDialog open onClose={() => setOffen(false)} />}
    </>
  );
}

/** Bearbeiten, beenden bzw. wieder aufnehmen, löschen — im Kopf des Aufrufs. */
export function AufrufAktionen({ aufruf }: { aufruf: AufrufView }) {
  const router = useRouter();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [laeuft, starte] = useTransition();

  function umschalten() {
    starte(async () => {
      const r = await aufrufAktivSetzen(aufruf.id, !aufruf.isActive);
      if ("error" in r && r.error) toast.error(r.error);
      else {
        toast.success(aufruf.isActive ? "Aufruf beendet" : "Aufruf wieder aufgenommen", {
          description: aufruf.isActive ? "Er steht nicht mehr auf der Website." : "Stell ihn bei Bedarf wieder auf die Website.",
        });
        router.refresh();
      }
    });
  }

  async function loeschen() {
    if (aufruf.donationCount > 0) {
      toast.error("Zu diesem Aufruf gibt es Spenden.", { description: "Beende ihn statt ihn zu löschen — die Spenden behalten ihren Bezug." });
      return;
    }
    const ja = await confirmDialog({
      title: "Aufruf löschen?",
      description: `„${aufruf.title}“ verschwindet. Spenden gibt es dazu noch keine.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ja) return;
    starte(async () => {
      const r = await aufrufLoeschen(aufruf.id);
      if ("error" in r && r.error) toast.error(r.error);
      else {
        toast.success("Aufruf gelöscht");
        router.push("/spenden/aufrufe");
      }
    });
  }

  return (
    <>
      <Button variant="outline" onClick={() => setBearbeiten(true)}>
        <Pencil className="h-4 w-4" aria-hidden />
        Bearbeiten
      </Button>
      <DropdownMenu trigger={<MehrKnopf aria-label="Weitere Aktionen" disabled={laeuft} />} align="right">
        <DropdownItem onClick={umschalten}>
          {aufruf.isActive ? <CirclePause className="h-4 w-4" aria-hidden /> : <CirclePlay className="h-4 w-4" aria-hidden />}
          {aufruf.isActive ? "Aufruf beenden" : "Wieder aufnehmen"}
        </DropdownItem>
        <DropdownSeparator />
        <DropdownItem variant="danger" onClick={loeschen}>
          <Trash2 className="h-4 w-4" aria-hidden />
          Löschen
        </DropdownItem>
      </DropdownMenu>
      {bearbeiten && <AufrufDialog open onClose={() => setBearbeiten(false)} aufruf={aufruf} />}
    </>
  );
}
